import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CreditCard, MapPin } from "lucide-react";

const TIME_SLOTS = ["ASAP", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00"];

const Checkout = () => {
  const { t } = useLang();
  const { items, subtotal, deliveryFee, amountToFreeTier, restaurant, clear } = useCart();
  const nav = useNavigate();

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    accommodation_type: "hotel", accommodation_name: "", room: "", address: "",
    instructions: "",
  });
  const [deliveryMode, setDeliveryMode] = useState("ASAP");
  const [customTime, setCustomTime] = useState("");
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState(0);
  const [placing, setPlacing] = useState(false);

  const total = subtotal + deliveryFee - discount;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const applyPromo = async () => {
    if (!promo) return;
    try {
      const r = await api.get(`/promotions/validate/${promo.trim().toUpperCase()}`);
      const d = subtotal * (r.data.discount_percent / 100);
      setDiscount(d);
      toast.success(`Promo applied: -€${d.toFixed(2)}`);
    } catch { toast.error(t("invalidPromo")); setDiscount(0); }
  };

  const useLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => {
      set("address", `Lat ${p.coords.latitude.toFixed(4)}, Lng ${p.coords.longitude.toFixed(4)}`);
      toast.success("Location detected");
    }, () => toast.error("Unable to get location"));
  };

  const place = async () => {
    if (!items.length || !restaurant) { toast.error("Basket is empty"); return; }
    for (const k of ["first_name", "last_name", "email", "phone", "address"]) {
      if (!form[k]) { toast.error(t("fillRequired")); return; }
    }
    setPlacing(true);
    try {
      const deliveryTime = deliveryMode === "ASAP" ? "ASAP" : (deliveryMode === "custom" ? customTime : deliveryMode);
      const payload = {
        restaurant_id: restaurant.id,
        restaurant_name: restaurant.name,
        items: items.map(i => ({
          item_id: i.item_id, name: i.name, qty: i.qty, price: i.price,
          addons: i.addons,
          removed_ingredients: i.removed_ingredients || [],
        })),
        subtotal, delivery_fee: deliveryFee, service_fee: 0, total,
        customer: { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone },
        delivery: {
          accommodation_type: form.accommodation_type,
          accommodation_name: form.accommodation_name,
          room: form.room,
          address: form.address,
          instructions: form.instructions,
        },
        delivery_time: deliveryTime,
        promo_code: promo || null,
        discount,
      };
      const r = await api.post("/orders", payload);
      toast.success(t("orderPlaced"));
      clear();
      nav(`/order-confirmation/${r.data.order_number}`);
    } catch (e) {
      toast.error("Failed to place order");
    } finally { setPlacing(false); }
  };

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading text-2xl text-navy">{t("emptyBasket")}</h1>
        <Button onClick={() => nav("/restaurants")} className="btn-coral mt-4">{t("browseRestaurants")}</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-navy tracking-tight">{t("continueGuest")}</h1>
      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2 space-y-6">

          {/* Contact */}
          <div className="bg-white rounded-2xl border border-navy/10 p-6">
            <h2 className="font-heading font-bold text-navy text-lg mb-4">Contact details</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>{t("firstName")}*</Label><Input value={form.first_name} onChange={e => set("first_name", e.target.value)} data-testid="checkout-firstname" /></div>
              <div><Label>{t("lastName")}*</Label><Input value={form.last_name} onChange={e => set("last_name", e.target.value)} data-testid="checkout-lastname" /></div>
              <div><Label>{t("email")}*</Label><Input type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="checkout-email" /></div>
              <div><Label>{t("phone")}*</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="checkout-phone" /></div>
            </div>
          </div>

          {/* Delivery */}
          <div className="bg-white rounded-2xl border border-navy/10 p-6">
            <h2 className="font-heading font-bold text-navy text-lg mb-4">{t("whereDeliver")}</h2>
            <div className="space-y-3">
              <div>
                <Label>{t("accommodationType")}</Label>
                <Select value={form.accommodation_type} onValueChange={v => set("accommodation_type", v)}>
                  <SelectTrigger data-testid="checkout-acctype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotel">{t("hotel")}</SelectItem>
                    <SelectItem value="apartment">{t("apartment")}</SelectItem>
                    <SelectItem value="villa">{t("villa")}</SelectItem>
                    <SelectItem value="other">{t("other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("accommodationName")}</Label><Input value={form.accommodation_name} onChange={e => set("accommodation_name", e.target.value)} data-testid="checkout-accname" /></div>
                <div><Label>{t("roomNumber")}</Label><Input value={form.room} onChange={e => set("room", e.target.value)} data-testid="checkout-room" /></div>
              </div>
              <div>
                <Label>{t("address")}*</Label>
                <div className="flex gap-2">
                  <Input value={form.address} onChange={e => set("address", e.target.value)} data-testid="checkout-address" />
                  <Button type="button" variant="outline" onClick={useLocation} className="shrink-0" data-testid="use-location-btn"><MapPin className="w-4 h-4" /></Button>
                </div>
              </div>
              <div><Label>{t("deliveryInstructions")}</Label><Textarea value={form.instructions} onChange={e => set("instructions", e.target.value)} placeholder={t("deliveryInstructionsPh")} data-testid="checkout-instructions" /></div>
            </div>
          </div>

          {/* Time */}
          <div className="bg-white rounded-2xl border border-navy/10 p-6">
            <h2 className="font-heading font-bold text-navy text-lg mb-4">{t("whenFood")}</h2>
            <RadioGroup value={deliveryMode === "ASAP" ? "asap" : "later"} onValueChange={v => setDeliveryMode(v === "asap" ? "ASAP" : "20:00")} className="mb-4">
              <div className="flex items-center gap-2 p-3 rounded-xl border border-navy/10 has-[:checked]:border-coral has-[:checked]:bg-coral/5">
                <RadioGroupItem value="asap" id="asap" data-testid="delivery-asap" />
                <Label htmlFor="asap" className="cursor-pointer flex-1">{t("immediate")} — {t("asap")}</Label>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-xl border border-navy/10 has-[:checked]:border-coral has-[:checked]:bg-coral/5">
                <RadioGroupItem value="later" id="later" data-testid="delivery-later" />
                <Label htmlFor="later" className="cursor-pointer flex-1">{t("orderLater")}</Label>
              </div>
            </RadioGroup>
            {deliveryMode !== "ASAP" && (
              <div className="flex flex-wrap gap-2">
                {TIME_SLOTS.filter(t => t !== "ASAP").map(slot => (
                  <button key={slot} onClick={() => setDeliveryMode(slot)} data-testid={`slot-${slot}`}
                    className={`px-4 py-2 rounded-full text-sm font-semibold ${deliveryMode === slot ? "bg-navy text-white" : "bg-sand-100 text-navy hover:bg-sand-200"}`}>{slot}</button>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs text-navy/60">{t("orderNow")}</p>
          </div>

          {/* Payment (mock) */}
          <div className="bg-white rounded-2xl border border-navy/10 p-6">
            <h2 className="font-heading font-bold text-navy text-lg mb-2 flex items-center gap-2"><CreditCard className="w-5 h-5" /> {t("payment")}</h2>
            <p className="text-xs text-navy/60">Demo mode — no real payment processing. Payment will be simulated on order confirmation.</p>
          </div>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-navy/10 p-6">
          <h3 className="font-heading font-bold text-navy text-lg mb-4">Order summary</h3>
          <div className="text-xs uppercase tracking-wider text-navy/60 mb-2">{restaurant?.name}</div>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {items.map(i => (
              <div key={i.key} className="flex justify-between text-sm">
                <span className="text-navy/80">{i.qty}× {i.name}</span>
                <span className="font-semibold">€{(i.price * i.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-navy/10 space-y-1 text-sm">
            <div className="flex justify-between"><span>{t("subtotal")}</span><span>€{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>{t("deliveryFee")}</span><span>€{deliveryFee.toFixed(2)}</span></div>
            {amountToFreeTier > 0 && (
              <div className="text-[11px] text-teal bg-teal/10 rounded-lg px-2 py-1.5 leading-tight" data-testid="checkout-delivery-hint">
                {t("deliveryHintAdd")} <b>€{amountToFreeTier.toFixed(2)}</b> {t("deliveryHintFor")} <b>€2.99</b> {t("deliveryHintDelivery")}
              </div>
            )}
            {discount > 0 && <div className="flex justify-between text-coral"><span>Discount</span><span>-€{discount.toFixed(2)}</span></div>}
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-navy/10"><span>{t("total")}</span><span data-testid="checkout-total">€{total.toFixed(2)}</span></div>
          </div>

          <div className="mt-4">
            <Label className="text-xs">{t("promoCode")}</Label>
            <div className="flex gap-2 mt-1">
              <Input value={promo} onChange={e => setPromo(e.target.value.toUpperCase())} placeholder="WELCOME10" data-testid="promo-input" />
              <Button type="button" variant="outline" onClick={applyPromo} data-testid="promo-apply">{t("apply")}</Button>
            </div>
          </div>

          <Button onClick={place} disabled={placing} className="btn-coral w-full mt-5 h-12" data-testid="place-order-btn">
            {placing ? "..." : t("placeOrder")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
